import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { browseExpandedStore } from '@/lib/browse-expanded-store'

const PERSIST_DEBOUNCE_MS = 300

type BrowseExpandedContextValue = {
  togglePath: (path: string) => void
  addPaths: (paths: string[]) => void
}

const BrowseExpandedContext = createContext<BrowseExpandedContextValue | null>(null)

export function BrowseExpandedProvider({
  initialPaths,
  onPathsChange,
  children,
}: {
  initialPaths: string[]
  /** Called debounced after expand toggles (localStorage persistence). */
  onPathsChange: (paths: string[]) => void
  children: ReactNode
}) {
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (!hydratedRef.current) {
      browseExpandedStore.hydrate(initialPaths)
      hydratedRef.current = true
    }
  }, [initialPaths])

  useEffect(() => {
    let debounceId: ReturnType<typeof setTimeout> | null = null
    const schedulePersist = () => {
      if (debounceId) clearTimeout(debounceId)
      debounceId = setTimeout(() => {
        onPathsChange(browseExpandedStore.getPathsArray())
      }, PERSIST_DEBOUNCE_MS)
    }
    const unsub = browseExpandedStore.subscribeGlobal(schedulePersist)
    return () => {
      if (debounceId) clearTimeout(debounceId)
      unsub()
    }
  }, [onPathsChange])

  const togglePath = useCallback((path: string) => {
    browseExpandedStore.toggle(path)
  }, [])

  const addPaths = useCallback((paths: string[]) => {
    browseExpandedStore.addPaths(paths)
  }, [])

  return (
    <BrowseExpandedContext.Provider value={{ togglePath, addPaths }}>
      {children}
    </BrowseExpandedContext.Provider>
  )
}

/** Per-folder expand state; re-renders only when this path toggles. */
export function useBrowsePathExpanded(path: string): boolean {
  return useSyncExternalStore(
    (onStoreChange) => browseExpandedStore.subscribe(path, onStoreChange),
    () => browseExpandedStore.isExpanded(path),
    () => false
  )
}

export function useBrowseExpandedContext(): BrowseExpandedContextValue | null {
  return useContext(BrowseExpandedContext)
}

export function useBrowseExpandedActions(): BrowseExpandedContextValue {
  const ctx = useBrowseExpandedContext()
  if (!ctx) {
    throw new Error('useBrowseExpandedActions must be used within BrowseExpandedProvider')
  }
  return ctx
}
